// data.ts — static demo data for login hero cards
import type { Breakpoint } from '../../hooks/useBreakpoint';

export interface BpConfig {
  gridCols: string;
  loginPanelWidth: string;
  dotCount: number;
  msspDotCount: number;
  dotSize: number;
  msspDotSize: number;
  dotGap: string;
  msspDotGap: string;
  gaugeSize: number;
  gradeClass: string;
  cisBarMax: string;
  carouselMinH: number;
  headlineSize: number;
  heroPadding: string;
  sparkHeight: number;
  msspSparkHeight: number;
  deltaLabelCol: string;
  cisRowFont: number;
  cisLabelCol: string;
}

export const bpConfig: Record<Breakpoint, BpConfig> = {
  desktop:     { gridCols: '1fr 380px', loginPanelWidth: '380px', dotCount: 888, msspDotCount: 3333, dotSize: 10, msspDotSize: 6, dotGap: '4px', msspDotGap: '2px', gaugeSize: 96, gradeClass: 'text-5xl', cisBarMax: '420px', carouselMinH: 380, headlineSize: 42, heroPadding: '36px 48px 28px', sparkHeight: 80, msspSparkHeight: 28, deltaLabelCol: '110px', cisRowFont: 11, cisLabelCol: '52px' },
  laptop:      { gridCols: '1fr 360px', loginPanelWidth: '360px', dotCount: 555, msspDotCount: 2200, dotSize: 8,  msspDotSize: 5, dotGap: '3px', msspDotGap: '2px', gaugeSize: 72, gradeClass: 'text-4xl', cisBarMax: '320px', carouselMinH: 340, headlineSize: 40, heroPadding: '32px 40px 24px', sparkHeight: 60, msspSparkHeight: 24, deltaLabelCol: '90px',  cisRowFont: 10, cisLabelCol: '46px' },
  smallLaptop: { gridCols: '1fr 340px', loginPanelWidth: '340px', dotCount: 400, msspDotCount: 1600, dotSize: 8,  msspDotSize: 5, dotGap: '3px', msspDotGap: '2px', gaugeSize: 64, gradeClass: 'text-3xl', cisBarMax: '280px', carouselMinH: 300, headlineSize: 36, heroPadding: '28px 32px 20px', sparkHeight: 50, msspSparkHeight: 22, deltaLabelCol: '80px',  cisRowFont: 9,  cisLabelCol: '42px' },
  tablet:      { gridCols: '1fr',       loginPanelWidth: '100%',  dotCount: 300, msspDotCount: 1000, dotSize: 8,  msspDotSize: 5, dotGap: '3px', msspDotGap: '2px', gaugeSize: 64, gradeClass: 'text-3xl', cisBarMax: '280px', carouselMinH: 260, headlineSize: 32, heroPadding: '24px 24px 16px', sparkHeight: 50, msspSparkHeight: 20, deltaLabelCol: '80px',  cisRowFont: 9,  cisLabelCol: '42px' },
  mobile:      { gridCols: '1fr',       loginPanelWidth: '100%',  dotCount: 200, msspDotCount: 600,  dotSize: 5,  msspDotSize: 4, dotGap: '2px', msspDotGap: '1px', gaugeSize: 56, gradeClass: 'text-2xl', cisBarMax: '240px', carouselMinH: 220, headlineSize: 28, heroPadding: '20px 16px 16px', sparkHeight: 40, msspSparkHeight: 18, deltaLabelCol: '70px',  cisRowFont: 8,  cisLabelCol: '38px' },
};

export interface CisControl {
  num: string;
  name: string;
  status: 'pass' | 'warn' | 'fail';
  pct: number;
  coverage: 'full' | 'partial';
}

export interface Org {
  name: string;
  letter: string;
  ep: number;
  pct: number;
  grade: string;
  alert: 'clear' | 'critical';
  alertText: string;
  color: string;
}

export const cisData: CisControl[] = [
  { num: 'CIS 1',  name: 'Enterprise Asset Inventory',  status: 'pass', pct: 94, coverage: 'partial' },
  { num: 'CIS 2',  name: 'Software Asset Inventory',    status: 'pass', pct: 96, coverage: 'full' },
  { num: 'CIS 3',  name: 'Data Protection',             status: 'warn', pct: 80, coverage: 'partial' },
  { num: 'CIS 4',  name: 'Secure Configuration',        status: 'warn', pct: 84, coverage: 'full' },
  { num: 'CIS 5',  name: 'Account Management',          status: 'warn', pct: 76, coverage: 'full' },
  { num: 'CIS 6',  name: 'Access Control Management',   status: 'pass', pct: 90, coverage: 'partial' },
  { num: 'CIS 7',  name: 'Vulnerability Management',    status: 'warn', pct: 88, coverage: 'full' },
  { num: 'CIS 8',  name: 'Audit Log Management',        status: 'pass', pct: 92, coverage: 'full' },
  { num: 'CIS 9',  name: 'Email & Browser Protections', status: 'pass', pct: 91, coverage: 'partial' },
  { num: 'CIS 10', name: 'Malware Defenses',            status: 'pass', pct: 97, coverage: 'full' },
  { num: 'CIS 11', name: 'Data Recovery',               status: 'fail', pct: 72, coverage: 'partial' },
  { num: 'CIS 12', name: 'Network Infrastructure',      status: 'pass', pct: 90, coverage: 'partial' },
  { num: 'CIS 13', name: 'Network Monitoring & Defense', status: 'pass', pct: 89, coverage: 'partial' },
];

const orgColors = ['var(--color-anchor-blue)', 'var(--color-anchor-purple)', 'var(--color-anchor-teal)', 'var(--color-anchor-violet)'];

export const orgs: Org[] = [
  { name: 'Acme Corp',         letter: 'A', ep: 210, pct: 91, grade: 'A-', alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Metro Legal',       letter: 'M', ep: 58,  pct: 88, grade: 'B+', alert: 'critical', alertText: '2 critical',  color: '' },
  { name: 'Riverside Med',     letter: 'R', ep: 185, pct: 72, grade: 'C+', alert: 'critical', alertText: '5 critical',  color: '' },
  { name: 'Downtown Dental',   letter: 'D', ep: 97,  pct: 94, grade: 'A',  alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Pinnacle Finance',  letter: 'P', ep: 202, pct: 86, grade: 'B',  alert: 'critical', alertText: '3 critical',  color: '' },
  { name: 'Lakewood Schools',  letter: 'L', ep: 165, pct: 79, grade: 'C+', alert: 'critical', alertText: '4 critical',  color: '' },
  { name: 'Cascade Logistics', letter: 'C', ep: 143, pct: 92, grade: 'A-', alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Summit Healthcare', letter: 'S', ep: 178, pct: 68, grade: 'D+', alert: 'critical', alertText: '8 critical',  color: '' },
  { name: 'Trident Insurance', letter: 'T', ep: 112, pct: 90, grade: 'A-', alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Beacon CPA',        letter: 'B', ep: 44,  pct: 95, grade: 'A',  alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Harbor Realty',      letter: 'H', ep: 76,  pct: 83, grade: 'B-', alert: 'critical', alertText: '1 critical',  color: '' },
  { name: 'Vanguard Mfg',      letter: 'V', ep: 134, pct: 77, grade: 'C',  alert: 'critical', alertText: '6 critical',  color: '' },
  { name: 'Northgate Auto',    letter: 'N', ep: 88,  pct: 89, grade: 'B+', alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Emerald Energy',    letter: 'E', ep: 102, pct: 81, grade: 'B-', alert: 'critical', alertText: '3 critical',  color: '' },
  { name: 'Whitfield Law',     letter: 'W', ep: 52,  pct: 96, grade: 'A',  alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Oakridge Dental',   letter: 'O', ep: 68,  pct: 93, grade: 'A-', alert: 'clear',    alertText: 'All clear',   color: '' },
  { name: 'Frontier Pharma',   letter: 'F', ep: 155, pct: 74, grade: 'C+', alert: 'critical', alertText: '7 critical',  color: '' },
  { name: 'Keystone Retail',   letter: 'K', ep: 153, pct: 85, grade: 'B',  alert: 'critical', alertText: '2 critical',  color: '' },
];
// Assign cycling colors
orgs.forEach((o, i) => { o.color = orgColors[i % orgColors.length]; });

// Raw color values for canvas drawing (CSS vars don't work in canvas)
export const sparkColors = [
  '#00CED1', '#6B3FA0', '#4545FF', '#8B5CF6', '#F59E0B', '#22C55E', '#EF4444',
  '#00CED1', '#6B3FA0', '#4545FF', '#8B5CF6', '#F59E0B', '#22C55E', '#EF4444',
  '#00CED1', '#6B3FA0', '#4545FF', '#8B5CF6',
];

// Raw color values for org icons (for inline styles where CSS vars may not resolve)
export const orgRawColors = ['#4545FF', '#6B3FA0', '#00CED1', '#8B5CF6'];

export const hostPrefixes = ['WS', 'PC', 'DT', 'LT', 'SRV', 'VM', 'NB'];
export const msspHostPrefixes = ['ACM', 'MET', 'RIV', 'DWN', 'PIN', 'LAK', 'CAS', 'SUM', 'TRI', 'BCN', 'HBR', 'VAN', 'NGA', 'EMR', 'WFL', 'OAK', 'FPH', 'KEY'];

export const timeCols = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7', '2 Wk', '3 Wk', '1 Mo'];

export function genHostname(i: number, isMssp: boolean): string {
  const prefixes = isMssp ? msspHostPrefixes : hostPrefixes;
  const pfx = prefixes[i % prefixes.length];
  return pfx + '-' + String(Math.floor(i / prefixes.length) + 1).padStart(3, '0');
}

export function genSparkline(start: number, end: number, volatility: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < 30; i++) {
    const t = i / 29;
    const base = start + (end - start) * t;
    const noise = (Math.random() - 0.5) * volatility;
    pts.push(Math.max(50, Math.min(100, Math.round(base + noise))));
  }
  pts[29] = end;
  return pts;
}

export function genDelta(basePct: number): number[] {
  const deltas: number[] = [];
  for (let i = 0; i < 7; i++) {
    const r = Math.random();
    if (basePct >= 95) deltas.push(r > 0.7 ? 0 : r > 0.3 ? 1 : -1);
    else if (basePct >= 85) deltas.push(r > 0.5 ? 0 : r > 0.25 ? Math.ceil(Math.random() * 2) : -Math.ceil(Math.random() * 2));
    else deltas.push(r > 0.4 ? Math.ceil(Math.random() * 3) : r > 0.2 ? 0 : -Math.ceil(Math.random() * 2));
  }
  const week1 = deltas.slice(0, 7).reduce((a, b) => a + b, 0);
  const week2 = week1 + Math.round((Math.random() - 0.3) * 4);
  const week3 = week2 + Math.round((Math.random() - 0.3) * 4);
  const month = week3 + Math.round((Math.random() - 0.25) * 5);
  deltas.push(week2, week3, month);
  return deltas;
}

export function gaugeOffset(pct: number): number {
  const circ = 2 * Math.PI * 20;
  return circ * (1 - pct / 100);
}

export function drawSparkline(canvas: HTMLCanvasElement, data: number[], color: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cellW = 14, cellH = 8, gap = 2;
  const minVal = 50, maxVal = 100;
  const maxRows = Math.floor(h / (cellH + gap));
  data.forEach((val, i) => {
    const filled = Math.round(((val - minVal) / (maxVal - minVal)) * maxRows);
    for (let r = 0; r < maxRows; r++) {
      const x = i * (cellW + gap);
      const y = h - (r + 1) * (cellH + gap);
      ctx.fillStyle = r < filled ? color : 'rgba(255,255,255,0.03)';
      ctx.globalAlpha = r < filled ? 0.7 : 1;
      ctx.beginPath();
      ctx.roundRect(x, y, cellW, cellH, 1.5);
      ctx.fill();
    }
  });
  ctx.globalAlpha = 1;
}

export function drawCompactSparkline(canvas: HTMLCanvasElement, data: number[], color: string): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cellW = 6, cellH = 4, gap = 1;
  const minVal = 50, maxVal = 100;
  const maxRows = Math.floor(h / (cellH + gap));
  data.forEach((val, i) => {
    const filled = Math.round(((val - minVal) / (maxVal - minVal)) * maxRows);
    for (let r = 0; r < maxRows; r++) {
      const x = i * (cellW + gap);
      const y = h - (r + 1) * (cellH + gap);
      ctx.fillStyle = r < filled ? color : 'rgba(255,255,255,0.03)';
      ctx.globalAlpha = r < filled ? 0.7 : 1;
      ctx.beginPath();
      ctx.roundRect(x, y, cellW, cellH, 0.5);
      ctx.fill();
    }
  });
  ctx.globalAlpha = 1;
}
